'use client';

import React, { useEffect, useState } from 'react';
import { useOfflineSync } from '@/hooks/useOfflineSync';

export default function OfflineBanner() {
  const { isOnline, isSyncing, pendingCount, lastSyncResult, lastSyncAt, triggerSync } = useOfflineSync();
  const [showSyncSuccess, setShowSyncSuccess] = useState(false);
  const [swRegistered, setSwRegistered] = useState(false);

  // Register service worker
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
    navigator.serviceWorker?.register('/sw.js', { scope: '/' })?.then((reg) => {
        setSwRegistered(true);
        console.log('[SW] Registered:', reg?.scope);
      })?.catch((err) => console.warn('[SW] Registration failed:', err));
  }, []);

  // Show success toast after sync
  useEffect(() => {
    if (lastSyncResult && lastSyncResult?.synced > 0) {
      setShowSyncSuccess(true);
      const t = setTimeout(() => setShowSyncSuccess(false), 5000);
      return () => clearTimeout(t);
    }
  }, [lastSyncResult]);

  // Offline banner
  if (!isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500/95 text-amber-950 px-4 py-2.5 flex items-center justify-between text-sm font-medium shadow-lg">
        <div className="flex items-center gap-2">
          <span className="text-base">📡</span>
          <span>
            <strong>Offline Mode</strong> — You&apos;re working offline. New records will be saved locally and synced when connectivity restores.
          </span>
        </div>
        {pendingCount > 0 && (
          <span className="bg-amber-900/20 border border-amber-900/30 rounded-full px-2.5 py-0.5 text-xs font-semibold">
            {pendingCount} pending
          </span>
        )}
      </div>
    );
  }

  // Syncing banner
  if (isSyncing) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-blue-600/95 text-white px-4 py-2.5 flex items-center gap-2 text-sm font-medium shadow-lg">
        <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span>Syncing offline records to Supabase…</span>
      </div>
    );
  }

  // Pending sync banner (online but has pending records)
  if (pendingCount > 0 && isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-violet-600/95 text-white px-4 py-2.5 flex items-center justify-between text-sm font-medium shadow-lg">
        <div className="flex items-center gap-2">
          <span className="text-base">🔄</span>
          <span>
            <strong>{pendingCount} offline record{pendingCount !== 1 ? 's' : ''}</strong> ready to sync to Supabase
          </span>
        </div>
        <button
          onClick={triggerSync}
          className="bg-white/20 hover:bg-white/30 border border-white/30 rounded-lg px-3 py-1 text-xs font-semibold transition-colors"
        >
          Sync Now
        </button>
      </div>
    );
  }

  // Sync success toast
  if (showSyncSuccess && lastSyncResult) {
    return (
      <div className="fixed top-4 right-4 z-[9999] bg-green-600/95 text-white px-4 py-3 rounded-xl flex items-center gap-2 text-sm font-medium shadow-xl border border-green-500/30">
        <span className="text-base">✅</span>
        <span>
          Synced <strong>{lastSyncResult?.synced}</strong> record{lastSyncResult?.synced !== 1 ? 's' : ''} to Supabase
          {lastSyncResult?.failed > 0 && (
            <span className="text-red-200 ml-1">({lastSyncResult?.failed} failed)</span>
          )}
        </span>
      </div>
    );
  }

  return null;
}
